import React, { useState } from "react";
import { Alert, Button, Card, Spinner, ProgressBar } from "react-bootstrap";
import { Database, CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { migrateToFirestore, verifyMigration } from "../utils/migrateToFirestore";
import Layout from "./Layout";

const MigrationTool = () => {
  const [migrating, setMigrating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [results, setResults] = useState(null);
  const [verificationResults, setVerificationResults] = useState(null);

  const handleMigrate = async () => {
    setMigrating(true);
    setResults(null);
    try {
      const migrationResults = await migrateToFirestore();
      setResults(migrationResults);
    } catch (error) {
      setResults({
        errors: [{ type: "General", error: error.message }],
        success: [],
        summary: { total: 0, caFirms: 0, admins: 0 }
      });
    } finally {
      setMigrating(false);
    }
  };

  const handleVerify = async () => {
    setVerifying(true);
    setVerificationResults(null);
    try {
      const verifyResults = await verifyMigration();
      setVerificationResults(verifyResults);
    } catch (error) {
      setVerificationResults({ error: error.message });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Layout>
      <div className="container py-4">
        <div className="d-flex align-items-center mb-4">
          <Database size={32} className="me-3 text-primary" />
          <div>
            <h1 className="h2 fw-bold mb-0">Database Migration Tool</h1>
            <p className="text-muted mb-0">
              Migrate data from Realtime Database to Firestore
            </p>
          </div>
        </div>

        <Alert variant="info" className="mb-4">
          <AlertTriangle size={20} className="me-2" />
          <strong>Important:</strong> This tool will copy all data from your
          Realtime Database (CA Firm and admin nodes) to Firestore under the
          ca_admin collection. Your original data will remain unchanged.
        </Alert>

        <Card className="mb-4">
          <Card.Body>
            <h5 className="card-title mb-3">Step 1: Run Migration</h5>
            <p className="text-muted">
              Click the button below to start migrating your data from Realtime
              Database to Firestore.
            </p>
            <Button
              variant="primary"
              onClick={handleMigrate}
              disabled={migrating}
              className="d-flex align-items-center"
            >
              {migrating ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Migrating...
                </>
              ) : (
                <>
                  <Database size={16} className="me-2" />
                  Start Migration
                </>
              )}
            </Button>

            {results && (
              <div className="mt-4">
                <Alert
                  variant={
                    results.errors.length === 0 ? "success" : "warning"
                  }
                >
                  <div className="d-flex align-items-center mb-2">
                    {results.errors.length === 0 ? (
                      <CheckCircle size={20} className="me-2" />
                    ) : (
                      <AlertTriangle size={20} className="me-2" />
                    )}
                    <strong>Migration Results</strong>
                  </div>
                  <ul className="mb-0">
                    <li>Total Records Migrated: {results.summary.total}</li>
                    <li>CA Firms: {results.summary.caFirms}</li>
                    <li>Admin Users: {results.summary.admins}</li>
                    <li>Errors: {results.errors.length}</li>
                  </ul>
                </Alert>

                {results.errors.length > 0 && (
                  <Alert variant="danger">
                    <strong>Errors:</strong>
                    <ul className="mb-0 mt-2">
                      {results.errors.map((error, index) => (
                        <li key={index}>
                          {error.type} {error.id && `(${error.id})`}: {error.error}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="mb-4">
          <Card.Body>
            <h5 className="card-title mb-3">Step 2: Verify Migration</h5>
            <p className="text-muted">
              After migration, verify that all data was transferred correctly.
            </p>
            <Button
              variant="outline-primary"
              onClick={handleVerify}
              disabled={verifying || !results}
              className="d-flex align-items-center"
            >
              {verifying ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle size={16} className="me-2" />
                  Verify Migration
                </>
              )}
            </Button>

            {verificationResults && (
              <div className="mt-4">
                {verificationResults.error ? (
                  <Alert variant="danger">
                    <XCircle size={20} className="me-2" />
                    Error: {verificationResults.error}
                  </Alert>
                ) : (
                  <Alert
                    variant={verificationResults.isMatch ? "success" : "warning"}
                  >
                    <div className="d-flex align-items-center mb-3">
                      {verificationResults.isMatch ? (
                        <CheckCircle size={20} className="me-2" />
                      ) : (
                        <AlertTriangle size={20} className="me-2" />
                      )}
                      <strong>
                        {verificationResults.isMatch
                          ? "Verification Successful!"
                          : "Verification Warning"}
                      </strong>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <h6>Realtime Database</h6>
                        <ul>
                          <li>CA Firms: {verificationResults.rtdbCounts.caFirms}</li>
                          <li>Admins: {verificationResults.rtdbCounts.admins}</li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <h6>Firestore</h6>
                        <ul>
                          <li>CA Firms: {verificationResults.firestoreCounts.caFirms}</li>
                          <li>Admins: {verificationResults.firestoreCounts.admins}</li>
                        </ul>
                      </div>
                    </div>
                  </Alert>
                )}
              </div>
            )}
          </Card.Body>
        </Card>

        <Alert variant="warning">
          <strong>After Successful Migration:</strong>
          <ol className="mb-0 mt-2">
            <li>Verify all data is correctly migrated</li>
            <li>Test your application thoroughly</li>
            <li>Once confirmed, you can optionally delete the old Realtime Database data</li>
            <li>Remove this migration tool from your application</li>
          </ol>
        </Alert>
      </div>
    </Layout>
  );
};

export default MigrationTool;
